/**
 * The access sentence: one plain-English line saying what a team member can do.
 *
 * "Chinelo can view incidents, report incidents and upload evidence in the Situation Room. Chinelo
 * can't change incident status or spend credits."
 *
 * The permission matrix is the input; this sentence is what people read, confirm and send to the
 * candidate. It is generated from the same key list the API receives, by a pure function, so it
 * cannot drift from what is actually granted. Every surface that shows or edits a member's access
 * renders it (the invite wizard's footer, the member panel, the owner's Team page).
 *
 * Never guesses pronouns from a name: it repeats the name, or says "they" when there is none.
 */

import { categorizeActions, humanizeActionName, type IActionCategory } from '@/right-panel/role/action-categories';

interface IModule {
	key: string;
	/** Heading in the editor's accordion. */
	label: string;
	/** Phrase appended to the verbs in the sentence; empty when the verbs already say where. */
	where: string;
}

/**
 * Longest prefix wins, so `election_result_analytics_view` lands in Result analytics, not Results.
 * Order here is the order modules appear in the sentence and the editor.
 */
const MODULES: IModule[] = [
	{ key: 'overview', label: 'Overview', where: '' },
	{ key: 'vesr', label: 'Situation Room', where: 'in the Situation Room' },
	{ key: 'voter_interaction', label: 'Voter interactions', where: 'in Voter interactions' },
	{ key: 'contacts', label: 'Contacts', where: '' },
	{ key: 'message_templates', label: 'Message templates', where: '' },
	{ key: 'voters', label: 'Voters', where: '' },
	{ key: 'candidate_media', label: 'Media gallery', where: '' },
	{ key: 'election_result_analytics', label: 'Result analytics', where: '' },
	{ key: 'election_result', label: 'Results', where: '' },
	{ key: 'result_verification', label: 'Result verification', where: '' },
	{ key: 'election_observers', label: 'Observers', where: '' },
	{ key: 'manage_agents', label: 'Agents', where: '' },
	{ key: 'party_agents', label: 'Agents', where: '' },
	{ key: 'my_wallet', label: 'Wallet', where: '' },
	// The real wallet keys are `wallet_*` (ACTIONS.MY_WALLET_VIEW = 'wallet_view'); without this the
	// sentence read "… and view for Ada Eze".
	{ key: 'wallet', label: 'Wallet', where: '' },
	{ key: 'support', label: 'Support desk', where: '' },
];

/** Verb phrases for the keys the templates use and their close neighbours. Lower case, no subject. */
const PHRASES: Record<string, string> = {
	overview: 'see the overview',
	overview_party_admin: 'see the overview',
	overview_party_candidate: 'see the overview',

	wallet_view: 'see the wallet',

	vesr_dashboard: 'see the dashboard',
	vesr_incidents: 'view incidents',
	vesr_incidents_create: 'report incidents',
	vesr_incidents_manage: 'update incidents and their status',
	vesr_media: 'view evidence',
	vesr_media_upload: 'upload evidence',
	vesr_reports: 'read reports',
	vesr_communication: 'send situation updates',
	vesr_messaging: 'use team chat',
	vesr_election_pulse: 'follow Election Pulse',
	vesr_efloc: 'look up polling locations',
	vesr_meetings: 'view meetings',
	vesr_meetings_create: 'schedule meetings',
	vesr_analytics: 'see analytics',
	vesr_feedback: 'read feedback',
	vesr_users: 'manage Situation Room users',
	vesr_settings: 'change Situation Room settings',
	vesr_integration: 'manage integrations',
	vesr_audit_logs: 'read audit logs',

	voter_interaction: 'view broadcasts',
	voter_interaction_messaging: 'message voters',

	contacts: 'open contacts',
	contacts_view: 'view contacts',
	contacts_manage: 'add and edit contacts',
	contacts_share: 'share contact lists',

	message_templates: 'use message templates',
	voters_view: 'look up voters',

	candidate_media: 'view campaign media',
	candidate_media_upload: 'upload campaign media',

	election_result_analytics: 'see result analytics',
	election_result_analytics_view: 'see result analytics',

	election_observers_manage: 'manage observers',
};

/**
 * Things worth saying a member CAN'T do: holding the first key without the second is the case
 * people ask about ("can she close incidents?").
 */
const NOTABLE_GAPS: { holds: string; lacks: string; phrase: string }[] = [
	{ holds: 'vesr_incidents', lacks: 'vesr_incidents_manage', phrase: 'change incident status' },
	{ holds: 'contacts_view', lacks: 'contacts_manage', phrase: 'edit contacts' },
	{ holds: 'vesr_media', lacks: 'vesr_media_upload', phrase: 'upload evidence' },
];

const MAX_VERBS_PER_MODULE = 4;

const moduleOf = (key: string): IModule => {
	const lower = key.toLowerCase();
	let best: IModule | null = null;
	MODULES.forEach((m) => {
		if ((lower === m.key || lower.startsWith(`${m.key}_`)) && (!best || m.key.length > best.key.length)) best = m;
	});
	if (best) return best;
	const head = lower.split('_')[0] || lower;
	const label = head.charAt(0).toUpperCase() + head.slice(1);
	return { key: head, label, where: '' };
};

const moduleRank = (module: IModule): number => {
	const index = MODULES.findIndex((m) => m.key === module.key);
	return index === -1 ? MODULES.length : index;
};

/** The verb phrase for one key, e.g. `vesr_media_upload` → "upload evidence". */
export const accessPhrase = (key: string): string => PHRASES[key.toLowerCase()] ?? humanizeActionName(key).toLowerCase();

/** The editor row label: the phrase, sentence case. */
export const accessLabel = (key: string): string => {
	const phrase = accessPhrase(key);
	return phrase.charAt(0).toUpperCase() + phrase.slice(1);
};

/** Accordion heading for a key's module. */
export const accessModuleLabel = (key: string): string => moduleOf(key).label;

/**
 * Group keys by module for the editor, through the shared `categorizeActions` (with a domain hint
 * per key, so the groups are product modules rather than raw prefixes like "Vesr"). Category keys
 * are module keys; `id` on each action is its index in `keys`.
 */
export const groupAccessKeys = (keys: readonly string[]): IActionCategory[] => {
	const unique = [...new Set(keys)];
	const categories = categorizeActions(
		unique.map((name, id) => ({ id, name, actionRoles: null, domain: moduleOf(name).key, category: 'general' }))
	);
	return categories
		.map((category) => {
			const sample = category.subcategories[0]?.actions[0]?.rawName ?? category.key;
			const module = moduleOf(sample);
			return {
				...category,
				label: module.label,
				subcategories: category.subcategories.map((sub) => ({
					...sub,
					actions: sub.actions
						.map((a) => ({ ...a, displayName: accessLabel(a.rawName) }))
						.sort((a, b) => a.displayName.localeCompare(b.displayName)),
				})),
			};
		})
		.sort((a, b) => {
			const ra = moduleRank(moduleOf(a.subcategories[0]?.actions[0]?.rawName ?? a.key));
			const rb = moduleRank(moduleOf(b.subcategories[0]?.actions[0]?.rawName ?? b.key));
			return ra - rb || a.label.localeCompare(b.label);
		});
};

export interface IDescribeAccessInput {
	/** First name. Empty → "This team member" / "They". */
	firstName?: string | null;
	actionKeys: readonly string[];
	/** e.g. "Situation Room desk" — shown in brackets after the name. */
	responsibility?: string | null;
	canSpendCredits: boolean;
	dailyCreditLimit?: number | null;
	electionCreditLimit?: number | null;
	/**
	 * True when the caller does not know the limits — a team member's own token carries only
	 * `CanSpendCredits`, and their allowance may not have loaded. The sentence then says nothing
	 * about limits instead of claiming "no limit set".
	 */
	creditLimitsUnknown?: boolean;
	/** Whose account the work is done in, e.g. "Ada Eze". Appended as "for Ada Eze". */
	forLabel?: string | null;
}

const formatNumber = (n: number) => n.toLocaleString('en-NG');

/** "a", "a and b", "a, b and c". */
export const joinAnd = (parts: readonly string[], conjunction = 'and'): string => {
	if (parts.length <= 1) return parts[0] ?? '';
	return `${parts.slice(0, -1).join(', ')} ${conjunction} ${parts[parts.length - 1]}`;
};

const capitalise = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

export const describeAccess = (input: IDescribeAccessInput): string => {
	const name = input.firstName?.trim() || '';
	const subject = name || 'This team member';
	const repeat = name || 'They';
	const responsibility = input.responsibility?.trim();
	const lead = responsibility ? `${subject} (${responsibility})` : subject;
	const keys = [...new Set(input.actionKeys.map((k) => k.trim()).filter(Boolean))];

	if (keys.length === 0) return `${lead} has no access yet.`;

	// Module → ordered, de-duplicated verb phrases.
	const byModule = new Map<string, { module: IModule; verbs: string[] }>();
	keys.forEach((key) => {
		const module = moduleOf(key);
		const entry = byModule.get(module.key) ?? { module, verbs: [] };
		const verb = accessPhrase(key);
		if (!entry.verbs.includes(verb)) entry.verbs.push(verb);
		byModule.set(module.key, entry);
	});

	const groups = [...byModule.values()].sort((a, b) => moduleRank(a.module) - moduleRank(b.module));
	const clauses = groups.map(({ module, verbs }) => {
		const shown =
			verbs.length > MAX_VERBS_PER_MODULE
				? [...verbs.slice(0, MAX_VERBS_PER_MODULE - 1), `${verbs.length - (MAX_VERBS_PER_MODULE - 1)} more things`]
				: verbs;
		return { text: module.where ? `${joinAnd(shown)} ${module.where}` : joinAnd(shown), multi: shown.length > 1 };
	});

	// A clause with its own "and" needs a stronger break: ", and" between two, semicolons among more.
	let body: string;
	if (clauses.length === 1) body = clauses[0].text;
	else if (clauses.length === 2 && clauses.some((c) => c.multi)) body = `${clauses[0].text}, and ${clauses[1].text}`;
	else if (clauses.some((c) => c.multi))
		body = `${clauses
			.slice(0, -1)
			.map((c) => c.text)
			.join('; ')}; and ${clauses[clauses.length - 1].text}`;
	else body = joinAnd(clauses.map((c) => c.text));

	const forLabel = input.forLabel?.trim();
	const sentences = [`${lead} can ${body}${forLabel ? ` for ${forLabel}` : ''}.`];

	const cannot = NOTABLE_GAPS.filter((gap) => keys.includes(gap.holds) && !keys.includes(gap.lacks)).map((gap) => gap.phrase);

	if (input.canSpendCredits) {
		const limits: string[] = [];
		if (input.dailyCreditLimit && input.dailyCreditLimit > 0) limits.push(`${formatNumber(input.dailyCreditLimit)} a day`);
		if (input.electionCreditLimit && input.electionCreditLimit > 0)
			limits.push(`${formatNumber(input.electionCreditLimit)} for the election`);
		sentences.push(
			limits.length
				? `${repeat} can spend credits, up to ${joinAnd(limits)}, but can't buy them.`
				: input.creditLimitsUnknown
					? `${repeat} can spend credits, but can't buy them.`
					: `${repeat} can spend credits with no limit set, but can't buy them.`
		);
	} else {
		cannot.push('spend credits');
	}

	if (cannot.length) sentences.push(`${capitalise(repeat)} can't ${joinAnd(cannot, 'or')}.`);

	return sentences.join(' ');
};
